import { environment } from '../../environments/environment';

/**
 * Which backend/database the client talks to. The user picks one at runtime (PROD or DEV); the
 * choice is per device and survives reloads. Everything that would otherwise mix the two
 * databases (auth token, IndexedDB, sync cursor) is scoped through this module.
 *
 * Requests are routed by rewriting `/api/...` to the selected target's path (see
 * `dataTargetInterceptor`); nginx on every host exposes both `/api-prod/` and `/api-dev/`.
 */
export type DataTarget = 'PROD' | 'DEV';

export const DATA_TARGETS: readonly DataTarget[] = ['PROD', 'DEV'];

const API_BASE: Record<DataTarget, string> = {
  PROD: '/api-prod',
  DEV: '/api-dev',
};

const STORAGE_KEY = 'data_target';

/** The `/api` path prefix the given target's requests are rewritten to. */
export function getApiBase(target: DataTarget = getDataTarget()): string {
  return API_BASE[target];
}

/** The build's home target: PROD build -> PROD, DEV build (dev.goul.org, local dev server) -> DEV. */
export function defaultDataTarget(): DataTarget {
  return environment.envLabel === 'PROD' ? 'PROD' : 'DEV';
}

export function getDataTarget(): DataTarget {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'PROD' || stored === 'DEV') return stored;
  } catch {
    // localStorage unavailable (private mode etc.) — fall through to the build default.
  }
  return defaultDataTarget();
}

export function isDevDataTarget(): boolean {
  return getDataTarget() === 'DEV';
}

/** Persists the choice and reloads into a clean state (nothing from the old target stays in memory). */
export function switchDataTarget(target: DataTarget): void {
  try {
    localStorage.setItem(STORAGE_KEY, target);
  } catch {
    return;
  }
  // Land on /home: the note/route the user was on belongs to the other database.
  window.location.assign(`${window.location.origin}/home`);
}

/**
 * Suffix that scopes per-target client state. The build's home target keeps the historic
 * un-suffixed names (so existing tokens / IndexedDB databases / sync cursors stay valid, with no
 * migration); the other target gets `_prod` / `_dev`. Storage is per-origin, so the two never collide.
 */
export function targetSuffix(target: DataTarget = getDataTarget()): string {
  return target === defaultDataTarget() ? '' : `_${target.toLowerCase()}`;
}

/** localStorage key for the JWT of the currently selected target. */
export function tokenKey(): string {
  return `access_token${targetSuffix()}`;
}

/** Scopes any per-backend localStorage key (e.g. `email`) to the selected target. */
export function targetScopedKey(baseKey: string): string {
  return `${baseKey}${targetSuffix()}`;
}

/** Rewrites a canonical `/api/...` URL to the selected target; any other URL is returned as-is. */
export function rewriteApiUrl(url: string): string {
  if (url === '/api' || url.startsWith('/api/') || url.startsWith('/api?')) {
    return API_BASE[getDataTarget()] + url.slice('/api'.length);
  }
  return url;
}
